import React, { useCallback, useEffect, useState } from 'react'
import { Grid, ViewMoreButton, ViewMoreContainer, NumberOfResultsText, GridWrapper } from './styles'
import { ProductTileSingleColumn } from 'src/components/ProductTiles/ProductTileSingleColumn'
import { ProductTileTwoColumns } from 'src/components/ProductTiles/ProductTileTwoColumns'
import { ProductTileTwoRowsTwoColumns } from 'src/components/ProductTiles/ProductTileTwoRowsTwoColumns'
import { get, has } from 'lodash'
import { useTranslation } from 'react-i18next'
import useQueryParams from 'src/hooks/useQueryParams'
import { useWindowWidth } from 'src/hooks/useWindowWidth'
import { breakpoints } from 'src/utils/styles'

const ProductGrid = ({
  styleFilter = 'all',
  colorFilter = 'all',
  sizeFilter = 'all',
  collectionFilter = 'all',
  promotionsFilter = 'all',
  sorting = 'default',
  products,
  setItemCount,
}) => {
  const { t } = useTranslation('misc')
  const { windowWidth } = useWindowWidth()

  const [pageSizeQuery, setPageSizeQuery] = useQueryParams('pageSize', 20)
  const maxItems = 20

  const [currentMaxShownItems, setCurrentMaxShownItems] = useState(parseInt(pageSizeQuery))
  const [actualShownItems, setActualShownItems] = useState(0)
  const [shopifyProducts, setShopifyProducts] = useState(products)

  const updateShownProductCount = useCallback(
    products => {
      if (products.length < currentMaxShownItems) {
        setActualShownItems(products.length)
      } else {
        products.some((e, index) => {
          if (index === currentMaxShownItems) {
            setActualShownItems(index)
            return true
          }
          return null
        })
      }
    },
    [currentMaxShownItems]
  )

  useEffect(() => {
    if (shopifyProducts.length < 1) return
    updateShownProductCount(shopifyProducts)
  }, [currentMaxShownItems, shopifyProducts, updateShownProductCount])

  useEffect(() => {
    let tmpProducts = products

    if (!styleFilter.includes('all')) {
      tmpProducts = tmpProducts.map(element => {
        const found = element.node.metafields.some(r => styleFilter.includes(r.value))
        if (found) return element
        else return null
      })
    }
    tmpProducts = tmpProducts.filter(e => {
      return e != null
    })

    if (!colorFilter.includes('all')) {
      tmpProducts = tmpProducts.map(element => {
        const found = element.node.metafields.some(field => {
          return field.key === 'color' && colorFilter.includes(field.value)
        })
        if (found) return element
        else return null
      })
    }

    tmpProducts = tmpProducts.filter(e => {
      return e != null
    })

    if (!sizeFilter.includes('all')) {
      tmpProducts = tmpProducts.map(element => {
        const found =
          element.node.options[0].values.some(r => sizeFilter.includes(r)) ||
          element.node.metafields.some(
            field => field.key === 'size' && sizeFilter.includes(field.value)
          )
        if (found === true && found !== undefined) return element
        else return null
      })
    }

    tmpProducts = tmpProducts.filter(e => {
      return e != null
    })

    if (!collectionFilter.includes('all')) {
      tmpProducts = tmpProducts.map(element => {
        const found = element.node.metafields.some(
          field => field.key === 'collection' && collectionFilter.includes(field.value)
        )
        if (found === true && found !== undefined) return element
        else return null
      })
    }

    tmpProducts = tmpProducts.filter(e => {
      return e != null
    })

    if (!promotionsFilter.includes('all')) {
      if (promotionsFilter.includes('Sale')) {
        tmpProducts = tmpProducts.map(element => {
          const found = element.node.variants.some(element => {
            return (
              parseFloat(element.compareAtPrice) !== 0 &&
              parseFloat(element.compareAtPrice) >= parseFloat(element.price)
            )
          })
          if (found === true && found !== undefined) return element
          else return null
        })
      }

      tmpProducts = tmpProducts.filter(e => {
        return e != null
      })

      if (promotionsFilter.includes('newIn')) {
        tmpProducts = tmpProducts.map(element => {
          if (parseInt(element.node.createdAt) <= 45) {
            return element
          }
          return null
        })
        tmpProducts = tmpProducts.filter(e => {
          return e != null
        })
      }
    }

    tmpProducts = tmpProducts.filter(e => {
      return e != null
    })

    tmpProducts = tmpProducts.sort(
      sorting === 'default'
        ? (a, b) => {
            if (a && a.node && a.node.createdAt && b && b.node && b.node.createdAt) {
              return a.node.createdAt - b.node.createdAt
            }
            return -1
          }
        : sorting === 'lowToHigh'
        ? (a, b) => a.node.variants[0].price - b.node.variants[0].price
        : sorting === 'highToLow'
        ? (a, b) => b.node.variants[0].price - a.node.variants[0].price
        : null
    )

    setShopifyProducts(tmpProducts)
    setItemCount(tmpProducts.length)
    updateShownProductCount(tmpProducts)
  }, [
    styleFilter,
    colorFilter,
    sizeFilter,
    collectionFilter,
    promotionsFilter,
    sorting,
    products,
    setItemCount,
    updateShownProductCount,
  ])

  const getTileSizeFromProduct = metafield => {
    const size = metafield.find(field => field.key === 'tileSize')
    if (size !== undefined) {
      return size.value
    }
    return '1x1'
  }

  let twoIndex = -3
  let fourIndex = 0

  return (
    <GridWrapper>
      <Grid>
        {shopifyProducts && shopifyProducts.length > 0 ? (
          shopifyProducts.map((product, index) => {
            if (has(product, 'node')) product = product.node
            if (index >= currentMaxShownItems) return null

            const fadeInImage =
              windowWidth > breakpoints.l && product.media.length > 1
                ? get(product.media, product.media.length - 1)
                : null

            if (getTileSizeFromProduct(product.metafields) === '2x1') {
              return (
                <ProductTileTwoColumns
                  key={product.id}
                  availableForSale={product.availableForSale}
                  index={index}
                  id={product.id}
                  title={product.title}
                  variants={product.variants}
                  handle={product.handle}
                  firstImage={get(product.images, 0)}
                  fadeInImage={fadeInImage}
                  createdAt={product.createdAt}
                  gridPosition={twoIndex}
                  metafields={product.metafields}
                />
              )
            }
            if (getTileSizeFromProduct(product.metafields) === '2x2') {
              return (
                <ProductTileTwoRowsTwoColumns
                  index={index}
                  availableForSale={product.availableForSale}
                  key={product.id}
                  id={product.id}
                  title={product.title}
                  variants={product.variants}
                  fadeInImage={fadeInImage}
                  handle={product.handle}
                  firstImage={get(product.images, 0)}
                  createdAt={product.createdAt}
                  gridPosition={fourIndex}
                  metafields={product.metafields}
                />
              )
            }
            if (getTileSizeFromProduct(product.metafields) === '1x1') {
              return (
                <ProductTileSingleColumn
                  key={product.id}
                  product={product}
                  fadeInImage={fadeInImage}
                />
              )
            }
            return null
          })
        ) : (
          <p style={{ marginTop: '3rem' }}>{t('noProductsFound')}</p>
        )}
      </Grid>
      {shopifyProducts.length > currentMaxShownItems ? (
        <ViewMoreContainer>
          <NumberOfResultsText>
            {actualShownItems} {t('of')} {shopifyProducts.length} {t('products')}
          </NumberOfResultsText>
          <ViewMoreButton
            onClick={() => {
              setPageSizeQuery(String(currentMaxShownItems + maxItems))
              setCurrentMaxShownItems(currentMaxShownItems + maxItems)
            }}
          >
            {t('viewMore')}
          </ViewMoreButton>
        </ViewMoreContainer>
      ) : null}
    </GridWrapper>
  )
}
export default ProductGrid
